import { Mapper, MapperParams } from './type.mapper';
import { Product, ShareClass } from '@models';
import {
  AssetClass,
  ConfigurationId,
  CurrencyCode,
  FundCategory,
  FundId,
  FundUmbrella,
  ProductDTO,
  ProductType,
  WebProductTaxonomy,
} from '@types';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { ShareClassMapper } from './share-class.type.mapper';
import { InvestmentTeamsMapper } from './investment-team.type.mapper';
import { AumMapper } from './aum.type.mapper';
import { getNameForSorting } from '@utils/sort-utils';

export class ProductsMapper extends Mapper<Product[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    productsList: ProductDTO[],
    activeList?: string[],
    overrideFlag?: boolean
  ): Product[] {
    const productsData: Product[] = [];

    productsList.forEach((productDto, index) => {
      const product: Product = this.createMapper(ProductMapper).toDomain(
        productDto,
        activeList,
        overrideFlag
      );

      if (product) {
        productsData.push(product);
      }

      // NGC-13646: SA - performance currency list.
      // Intentionally completed the ReplaySubject defined as 'currencyListByPerformance$' in site config service.
      // This will mark complete the subscription defined in 'getCurrencyListByPerformance' to splice 'SHARE_CLASS' as first option.
      if (
        this.mapperParams.config.getPdsCountry() === 'ZA' &&
        index === productsList.length - 1
      ) {
        this.mapperParams.config.currencyListByPerformance$.complete();
      }
    });
    return productsData;
  }
}

export class ProductMapper extends Mapper<Product> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    productDto: ProductDTO,
    activeList?: string[],
    overrideFlag?: boolean
  ): Product {
    let product: Product;
    const productShareClasses: ShareClass[] = [];

    productDto?.shareclass?.forEach((shareClassDto) => {
      const shareClass = this.createMapper(ShareClassMapper).toDomain(
        shareClassDto,
        productDto.producttype as ProductType,
        productDto.webprdcttaxonomy as WebProductTaxonomy,
        productDto.basecurrcode,
        productDto.fundname,
        activeList,
        overrideFlag
      );
      if (shareClass) {
        productShareClasses.push(shareClass);
      }
    });

    product = {
      fundId: productDto.fundid as FundId,
      fundName: productDto.fundname,
      countryCode: productDto.cntrycode,
      fundNameForSorting:
        productDto.fundname &&
        !this.mapperParams?.config.isSiteInternational() &&
        (this.mapperParams?.configurationName === ConfigurationId._529 ||
          this.mapperParams?.configurationName === ConfigurationId.SMA)
          ? getNameForSorting(productDto.fundname)
          : '',
      assetClass: productDto.assetclass as AssetClass,
      investmentCategory: productDto.invstmntcatg,
      fundCategory: productDto.fundcatg as FundCategory,
      investmentManager: productDto.invmangr,
      sfdrCategory: productDto.sfdrcategory,
      investmentVehicle: productDto.invvehcile,
      fundUmbrella: productDto.fundumbrlla as FundUmbrella,
      region: productDto.region,
      investmentGroup: productDto.invstmntgroup,
      currencyCode: productDto.basecurrcode as CurrencyCode,
      etfType: productDto.etftype,
      strategy: productDto.strategy,
      methodology: productDto.methodology,
      fundFamily: productDto.fundfamily,
      productType: productDto.producttype as ProductType,
      productTaxonomy: productDto.webprdcttaxonomy as WebProductTaxonomy,
      shareClasses: productShareClasses,
      investmentTeam: productDto.investmentteam
        ? this.createMapper(InvestmentTeamsMapper).toDomain(
            productDto.investmentteam
          )
        : null,
      aum: productDto.aum
        ? this.createMapper(AumMapper).toDomain(productDto.aum)
        : null,
    };
    return product;
  }
}
